/**
 * Get all preceding siblings of each element in the set of matched elements, optionally filtered by a selector.
 * The children are returned in reverse order, with the first element being the one closest to the current one.
 * @param { HTMLElement } element - The element whose siblings will be checked.
 * @param { string } selector - The selector to match against.
 */
export const prevAll = (
  element: HTMLElement,
  selector: string
): Array<Element> => {
  if (!element) {
    return []
  }

  const parent = element.parentNode

  if (!parent) {
    return []
  }

  const allChildren = Array.from(parent.children)
  const index = allChildren.indexOf(element)
  return allChildren
    .slice(0, index)
    .filter((child) => !selector || child.matches(selector))
    .reverse()
}

/**
 * Get all following siblings of each element in the set of matched elements, optionally filtered by a selector.
 * @param { HTMLElement } element - The element whose siblings will be checked.
 * @param { string } selector - The selector to match against.
 */
export const nextAll = (
  element: HTMLElement,
  selector: string
): Array<Element> => {
  if (!element) {
    return []
  }

  const parent = element.parentNode

  if (!parent) {
    return []
  }

  const allChildren = Array.from(parent.children)
  const index = allChildren.indexOf(element)
  return allChildren
    .slice(index + 1)
    .filter((child) => !selector || child.matches(selector))
}

/**
 * Makes an event handler that applies the handler callback to the closest element
 * that matches the provided selector
 * @param {Function} handler - The callback to execute
 * @param {string} selector - Filters to get the closest element that matches the selector
 * if no match happens, then the handler is not triggered at all
 * @returns
 */
const makeClosestEventHandler =
  (handler: (event: Event) => void, selector: string) =>
  (event: Event): void => {
    if (!event.target || typeof handler !== 'function') {
      return
    }

    const target = event.target as HTMLElement

    if (target.closest(selector)) {
      handler(event)
    }
  }

/**
 * A pure JS alternative for the $(...).on delegated event listener provided by JQuery
 * @param {HTMLElement} element - The element the delegated listener is attached upon
 * @param {string} type - The event to listen for
 * @param {string} selector - A selector to narrow down the specific element we want to watch for
 * emitting the event
 * @param {Function} handler - The callback to execute when the conditions are met
 */
export const onDocumentEvent = (
  element: Window | HTMLElement | Document,
  type: string,
  selector: string | null,
  handler: (event: Event) => void
): (() => void) => {
  const closestEventHandler = selector
    ? makeClosestEventHandler(handler, selector)
    : // No selector defined, invoke handler directly
      handler

  element.addEventListener(type, closestEventHandler)

  return () => {
    element.removeEventListener(type, closestEventHandler)
  }
}
